import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { Demande, RetourDemande, RetourRemplacement } from '../../types';
import { formatDateCourt } from './format';
import { FraEdition } from './FraEdition';

import { dePersonne } from '../messages/format';
// Étape 7 « Retour » (docs/DECISIONS.md §23) : après la séance, la personne qui a remplacé transmet ses faits marquants, les
// protocoles appliqués et la liste de présence à la personne remplacée ; la gestion contrôle puis clôture. Le retour reste PRIVÉ :
// seules la personne remplacée, la personne qui a remplacé et la gestion le lisent. Qui peut quoi : server/utils/retour.ts.

const prenomDe = (nom: string | null | undefined): string => (nom ?? '').trim().split(/\s+/)[0] || 'la personne remplacée';

export function RetourCarte({ demande, info, estAbsent, peutGerer, onChange, onOuvrirPresences }: {
  demande: Demande;
  info: RetourDemande;
  /** La personne qui regarde EST la personne remplacée. */
  estAbsent: boolean;
  peutGerer: boolean;
  onChange: () => void;
  /** Gestion : « Contrôler et clôturer » ouvre l'onglet Présences de la même fiche. */
  onOuvrirPresences: () => void;
}) {
  const { droits, retour } = info;
  const transmis = retour?.transmis_at != null;
  const absentNom = demande.absent_nom ?? 'la personne remplacée';
  const remplacantNom = info.remplacant_nom ?? 'La personne qui a remplacé';
  // En complément (« Retour de … ») : l'élision sur un vrai nom, « de la personne qui a remplacé » à défaut (le repli capitalisé ne sert que de sujet).
  const deRemplacant = info.remplacant_nom ? dePersonne(info.remplacant_nom) : 'de la personne qui a remplacé';
  const seance = `${demande.atelier_nom ?? 'Atelier'}, ${formatDateCourt(demande.date_atelier)}`;
  const seancePhrase = seance.endsWith('.') ? seance : `${seance}.`;   // « mar. 13 oct. » porte déjà son point
  // La personne qui écrit est la personne qui a remplacé (ou la gestion à sa place).
  const estAuteur = droits.ecrire && !peutGerer;

  const [faits, setFaits] = useState(retour?.faits_marquants ?? '');
  const [protocoles, setProtocoles] = useState(retour?.protocoles ?? '');
  const [presence, setPresence] = useState(retour?.presence_remplie ?? false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmerRien, setConfirmerRien] = useState(false);
  const [saisieGestion, setSaisieGestion] = useState(false);
  const [lire, setLire] = useState(false);
  const [fra, setFra] = useState(false);

  // Brouillon : le texte n'est jamais perdu si l'on quitte la fiche avant d'envoyer.
  const enregistrerBrouillon = (patch: Partial<RetourRemplacement> = {}) => {
    api.enregistrerRetour(demande.id, { faits_marquants: faits, protocoles, presence_remplie: presence, ...patch }).catch(() => {});
  };

  async function transmettre(rien: boolean) {
    setEnCours(true);
    setErreur(null);
    try {
      await api.transmettreRetour(demande.id, rien
        ? { rien_a_signaler: true, faits_marquants: null, protocoles: null, presence_remplie: presence }
        : { rien_a_signaler: false, faits_marquants: faits, protocoles, presence_remplie: presence });
      setConfirmerRien(false);
      onChange();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "Le retour n'a pas pu être transmis. Réessayez.");
    } finally {
      setEnCours(false);
    }
  }

  if (!droits.lire) return null;

  // ── 1. À écrire : la personne qui a remplacé (ou la gestion, à sa place) ─────────────────────────────────────────────
  if (droits.ecrire && (estAuteur || saisieGestion)) {
    const vide = !faits.trim() && !protocoles.trim();
    return (
      <section className="retour-carte" aria-labelledby="retour-titre">
        <span className="retour-qui">{estAuteur ? 'Retour · À vous de jouer' : 'Retour · Saisie par la gestion'}</span>
        <h3 id="retour-titre" className="retour-titre">
          {estAuteur ? `Transmettez votre retour à ${absentNom}` : `Retour ${deRemplacant} à ${absentNom}`}
        </h3>
        <p className="retour-sous">{seancePhrase} Ce retour n'est lu que par {prenomDe(absentNom)} et l'équipe gestion.</p>
        <div className="form-group">
          <label className="form-label" htmlFor="retour-faits">Faits marquants</label>
          <textarea id="retour-faits" className="form-textarea" rows={3} value={faits} maxLength={4000}
            placeholder="Ex. : séance déplacée dans une autre salle, matériel manquant, consigne à transmettre…"
            onChange={(e) => setFaits(e.target.value)} onBlur={() => enregistrerBrouillon()} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="retour-protocoles">Protocoles appliqués</label>
          <textarea id="retour-protocoles" className="form-textarea" rows={3} value={protocoles} maxLength={4000}
            placeholder="Ex. : consigne de sécurité appliquée, protocole d'évacuation…"
            onChange={(e) => setProtocoles(e.target.value)} onBlur={() => enregistrerBrouillon()} />
        </div>
        <p className="text-xs text-ink-3" id="retour-aide-participants" style={{ marginTop: 0 }}>
          N'écrivez ici aucune information médicale ni aucun fait sur un·e participant·e : ils se notent dans le journal des participant·e·s d'Eveil.
        </p>
        <label className="flex items-center gap-8 text-sm">
          <input type="checkbox" checked={presence} onChange={(e) => { setPresence(e.target.checked); enregistrerBrouillon({ presence_remplie: e.target.checked }); }} />
          Liste de présence remplie
        </label>
        {erreur && <div className="badge badge-err" role="alert" style={{ display: 'block', textTransform: 'none', letterSpacing: 'normal' }}>{erreur}</div>}
        {confirmerRien ? (
          <div className="retour-confirmation" role="alertdialog" aria-label="Confirmer : rien à signaler">
            <span className="text-sm">Envoyer « Rien à signaler » à {prenomDe(absentNom)} et à l'équipe gestion ? Il ne pourra plus être modifié.</span>
            <div className="flex gap-8">
              <button type="button" className="btn btn-accent" disabled={enCours} onClick={() => transmettre(true)}>
                {enCours ? <span className="spinner spinner-sm" /> : 'Oui, envoyer'}
              </button>
              <button type="button" className="btn btn-ghost" disabled={enCours} onClick={() => setConfirmerRien(false)}>Annuler</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-12 flex-wrap">
            <button type="button" className="btn btn-accent" disabled={enCours || vide} onClick={() => transmettre(false)}>
              {enCours ? <span className="spinner spinner-sm" /> : 'Envoyer mon retour'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={enCours} onClick={() => setConfirmerRien(true)}>Rien à signaler</button>
            {vide && <span className="text-xs text-ink-3">Décrivez au moins un point, ou choisissez « Rien à signaler ».</span>}
            {!estAuteur && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSaisieGestion(false)}>Annuler la saisie</button>}
          </div>
        )}
      </section>
    );
  }

  // ── 2. Transmis : lecture, selon qui regarde ─────────────────────────────────────────────────────────────────────────
  if (transmis && retour) {
    const rien = retour.rien_a_signaler;
    return (
      <section className="retour-carte retour-carte-lecture" aria-labelledby="retour-titre">
        <span className="retour-qui">{estAbsent ? 'Retour · Pour vous' : peutGerer ? 'Retour · Équipe gestion' : 'Retour · Transmis'}</span>
        <h3 id="retour-titre" className="retour-titre">
          {estAbsent
            ? `Le retour ${deRemplacant} sur votre atelier`
            : peutGerer
              ? `Retour transmis à ${absentNom} · liste de présence ${info.presence_recue ? 'reçue' : 'à recevoir'}`
              : `Merci : votre retour est transmis à ${absentNom} et à l'équipe gestion`}
        </h3>
        <p className="retour-sous">
          {rien
            ? (estAbsent ? `${seancePhrase} ${remplacantNom} n'a rien à signaler pour cette séance.` : `${seancePhrase} Rien à signaler pour cette séance.`)
            : `${seancePhrase} Faits marquants, protocoles appliqués et liste de présence${estAbsent ? '' : ' de la séance'}.`}
        </p>
        <div className="flex items-center gap-12 flex-wrap">
          <button type="button" className="btn" onClick={() => setLire(true)}>{estAuteur || (!estAbsent && !peutGerer) ? 'Relire mon retour' : 'Lire le retour'}</button>
          {peutGerer && demande.statut === 'atelier_donne' && (
            <button type="button" className="btn btn-accent" onClick={onOuvrirPresences}>Contrôler et clôturer</button>
          )}
          {estAbsent && !rien && (
            <button type="button" className="retour-lien" onClick={() => setFra(true)}>Mettre à jour ma FRA avec ce retour</button>
          )}
        </div>
        {lire && (
          <LireRetour retour={retour} qui={remplacantNom} seance={seance} presenceRecue={info.presence_recue}
            onFermer={() => setLire(false)}
            onFra={estAbsent && !rien ? () => { setLire(false); setFra(true); } : undefined} />
        )}
        {fra && <FraEdition atelierId={demande.atelier_id} atelierNom={demande.atelier_nom ?? 'Atelier'} retour={retour} qui={remplacantNom} onClose={() => setFra(false)} />}
      </section>
    );
  }

  // ── 3. En attente : la séance est passée, le retour n'est pas encore là ────────────────────────────────────────────
  if (demande.statut !== 'atelier_donne') return null;
  return (
    <section className="retour-carte retour-carte-attente" aria-labelledby="retour-titre">
      <span className="retour-qui">Retour · En attente</span>
      <h3 id="retour-titre" className="retour-titre">{remplacantNom} n'a pas encore transmis son retour</h3>
      <p className="retour-sous">
        {estAbsent ? 'Vous serez prévenu·e dès qu\'il arrive.' : 'Elle ou il reçoit un rappel le lendemain de la séance. Vous pouvez aussi le saisir à sa place.'}
      </p>
      {peutGerer && droits.ecrire && (
        <div><button type="button" className="btn" onClick={() => setSaisieGestion(true)}>Saisir le retour à sa place</button></div>
      )}
    </section>
  );
}

/** Le retour, par-dessus la fiche (✕, Échap). Une demande « rien à signaler » le dit en clair. */
function LireRetour({ retour, qui, seance, presenceRecue, onFermer, onFra }: {
  retour: RetourRemplacement; qui: string; seance: string; presenceRecue: boolean; onFermer: () => void; onFra?: () => void;
}) {
  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [onFermer]);
  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="lire-retour-titre" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="lire-retour-titre">Retour {dePersonne(qui)}</h3>
          <button type="button" className="close-btn" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          <p className="text-xs text-ink-3" style={{ marginTop: 0 }}>{seance}</p>
          {retour.rien_a_signaler ? (
            <p className="text-sm text-ink-1">Rien à signaler pour cette séance.</p>
          ) : (
            <>
              {retour.faits_marquants && (<><h4 className="retour-h">Faits marquants</h4><p className="retour-texte">{retour.faits_marquants}</p></>)}
              {retour.protocoles && (<><h4 className="retour-h">Protocoles appliqués</h4><p className="retour-texte">{retour.protocoles}</p></>)}
            </>
          )}
          <p className="text-sm text-ink-2">Liste de présence : {presenceRecue || retour.presence_remplie ? '✓ remplie' : 'pas encore indiquée comme remplie'}.</p>
        </div>
        <div className="modal-footer">
          {onFra && <button type="button" className="btn btn-sm btn-ghost" onClick={onFra}>Mettre à jour ma FRA avec ce retour</button>}
          <button type="button" className="btn btn-sm btn-accent" onClick={onFermer} autoFocus>Fermer</button>
        </div>
      </div>
    </div>
  );
}
